import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { WorkTeamsDeleteDialog } from 'pages/Finance/WorkTeams/components/WorkTeamsDeleteDialog';
import { useEditWorkGroupAccess } from 'pages/Finance/WorkTeams/hooks/useEditWorkGroupAccess';
import { RecordContextProvider, WrapperField } from 'react-admin';
import { Button, CreateButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { ChipField, EmbeddedFunctionField, TextField } from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { ListPage } from 'shared/react-admin/Pages';
import { Country } from 'types/embedded/country';
import { Permission } from 'types/embedded/permission';
import { WorkTeam } from 'types/embedded/workTeam';
import { getUserName } from 'utils';

import { StyledDatagridWithStickyActions } from './WorkTeamsList.styled';
import { WorkTeamsListFilter } from './WorkTeamsListFilter';
import { WorkTeamsListInfoDialog } from './WorkTeamsListInfoDialog/WorkTeamsListInfoDialog';
import { WorkTeamsListMembersAvatars } from './WorkTeamsListMembersAvatars';
import { WorkTeamsListNameField } from './WorkTeamsListNameField/WorkTeamsListNameField';

export const WorkTeamsList = () => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<WorkTeam>();
  const [isOpenInfoDialog, setIsOpenInfoDialog] = useState(false);
  const { hasEditWorkGroupAccess } = useEditWorkGroupAccess();

  return (
    <ListPage
      actions={[
        <Button
          key="help"
          label="Справка"
          onClick={() => setIsOpenInfoDialog(true)}
          startIcon={<InfoOutlinedIcon />}
          variant="outlined"
        />,
        hasResourcePermissions(Resources.WorkTeams, [Permission.Full, Permission.Create]) && (
          <CreateButton key="create-comissions" />
        ),
      ]}
      empty={false}
      headerTitle={{ titleText: 'Рабочие группы' }}
      listBoxProps={{ sx: { marginTop: '16px' } }}
    >
      <WorkTeamsListFilter />
      <StyledDatagridWithStickyActions bulkActionButtons={false}>
        <WorkTeamsListNameField label="Название группы" source="name" />
        <EmbeddedArrayField
          cellClassName="column-countries"
          label="Страны"
          render={(values: Country[]) =>
            values.map((v) => (
              <ChipField
                color="default"
                key={v?.id}
                record={v}
                source="name"
                sx={{ marginRight: '4px', marginBottom: '4px' }}
              />
            ))
          }
          sortable={false}
          sourceField="countries"
        />

        <EmbeddedFunctionField
          cellClassName="column-manager"
          label="Руководитель группы"
          render={getUserName}
          sourceField="manager"
        />
        <EmbeddedArrayField
          cellClassName="column-members"
          label="Участники"
          render={(values) => {
            return (
              <RecordContextProvider value={values}>
                <WorkTeamsListMembersAvatars />
              </RecordContextProvider>
            );
          }}
          sortable={false}
          sourceField="members"
        />
        <TextField emptyText={EMPTY_FIELD} label="Reddy ID канала" source="reddyChatId" />
        <WrapperField cellClassName="actions">
          <ContextMenu
            render={(record: WorkTeam) => {
              return [
                hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton
                    entityName="work_team"
                    key="user-action-button"
                  />
                ),
                <ContextMenuShowButton key="show-button" />,

                hasEditWorkGroupAccess(record) &&
                  hasResourcePermissions(Resources.WorkTeams, [
                    Permission.Full,
                    Permission.Update,
                  ]) && <ContextMenuEditButton key="edit-button" />,
                hasEditWorkGroupAccess(record) &&
                  hasResourcePermissions(Resources.WorkTeams, [
                    Permission.Full,
                    Permission.Delete,
                  ]) && (
                    <ContextMenuDeleteButton
                      key="delete-button"
                      onClick={() => handleOpenRecordModalDelete(record)}
                    />
                  ),
              ];
            }}
          />
        </WrapperField>
      </StyledDatagridWithStickyActions>
      {openRecordModalDelete && (
        <WorkTeamsDeleteDialog
          onClose={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          record={recordDelete}
        />
      )}
      {isOpenInfoDialog && (
        <WorkTeamsListInfoDialog
          onClose={() => setIsOpenInfoDialog(false)}
          open={isOpenInfoDialog}
        />
      )}
    </ListPage>
  );
};
